import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";

interface MediaItem {
  id: number;
  type: "image" | "video";
  src: string;
  thumbnail?: string;
}

const mediaItems: MediaItem[] = [
  { id: 1, type: "image", src: "/images/1.jpeg" },
  { id: 2, type: "image", src: "/images/2.jpeg" },
  { id: 3, type: "image", src: "/images/3.jpeg" },
  { id: 4, type: "image", src: "/images/4.jpeg" },
  { id: 5, type: "image", src: "/images/5.jpeg" },
  { id: 6, type: "image", src: "/images/6.jpeg" },
  { id: 7, type: "image", src: "/images/7.jpeg" },
  { id: 8, type: "image", src: "/images/8.jpeg" },
  { id: 9, type: "image", src: "/images/9.jpeg" },
  { id: 10, type: "image", src: "/images/10.jpeg" },
  { id: 11, type: "image", src: "/images/11.jpeg" },
  { id: 12, type: "image", src: "/images/12.jpeg" },
  { id: 13, type: "image", src: "/images/13.jpeg" },
  { id: 14, type: "image", src: "/images/14.jpeg" },
  { id: 15, type: "image", src: "/images/Braj-5.jpeg" },
  { id: 16, type: "image", src: "/images/Braj-4.jpeg" },

  { id: 17, type: "video", src: "/images/Braj-1.mp4", thumbnail: "/images/1.jpeg" },
  { id: 18, type: "video", src: "/images/Braj-2.mp4", thumbnail: "/images/18.jpeg" },
  { id: 19, type: "video", src: "/images/Braj-3.mp4", thumbnail: "/images/10.jpeg" },
  { id: 20, type: "video", src: "/images/Braj-6.mp4", thumbnail: "/images/11.jpeg" },
  { id: 21, type: "video", src: "/images/Braj-7.mp4", thumbnail: "/images/9.jpeg" },
  { id: 22, type: "video", src: "/images/Braj-8.mp4", thumbnail: "/images/4.jpeg" },
  { id: 23, type: "video", src: "/images/Braj-9.mp4", thumbnail: "/images/3.jpeg" },
  { id: 24, type: "video", src: "/images/Braj-10.mp4", thumbnail: "/images/13.jpeg" },
  { id: 25, type: "image", src: "/images/b-1.jpeg" },
  { id: 26, type: "image", src: "/images/b-2.jpeg" },
  { id: 27, type: "image", src: "/images/b-3.jpeg" },
  { id: 28, type: "image", src: "/images/b-4.jpeg" },
  { id: 29, type: "image", src: "/images/b-5.jpeg" },
  { id: 30, type: "image", src: "/images/b-6.jpeg" },
  { id: 31, type: "image", src: "/images/b-7.jpeg" },
  { id: 32, type: "image", src: "/images/b-8.jpeg" },
  { id: 33, type: "image", src: "/images/b-9.jpeg" },
  { id: 34, type: "image", src: "/images/b-10.jpeg" },
  { id: 35, type: "image", src: "/images/b-11.jpeg" },
  { id: 36, type: "image", src: "/images/b-12.jpeg" },
  { id: 37, type: "image", src: "/images/b-13.jpeg" },
  { id: 38, type: "image", src: "/images/b-14.jpeg" },
  { id: 39, type: "image", src: "/images/b-15.jpeg" },
  { id: 40, type: "image", src: "/images/b-16.jpeg" },
  { id: 41, type: "image", src: "/images/c-1.jpg" },
  { id: 42, type: "image", src: "/images/c-2.jpg" },
  { id: 43, type: "image", src: "/images/c-3.jpg" },
  { id: 44, type: "image", src: "/images/c-4.jpg" },
  { id: 45, type: "image", src: "/images/c-5.jpg" },

];

const Gallery: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "image" | "video">("all");
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const filteredItems =
    filter === "all"
      ? mediaItems
      : mediaItems.filter((i) => i.type === filter);

  const next = () =>
    setCurrentIndex((i) => (i! + 1) % filteredItems.length);

  const prev = () =>
    setCurrentIndex((i) =>
      i! === 0 ? filteredItems.length - 1 : i! - 1
    );

  /* 🔑 KEYBOARD SUPPORT */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") setCurrentIndex(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentIndex]);

  return (
    <>
      {/* HERO */}
      <div className="relative h-[40vh] md:h-[55vh]">
        <img
          src="/images/1.jpeg"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
          <h1 className="text-4xl md:text-5xl font-bold text-yellow-400">
            Gallery
          </h1>

          {/* FILTER */}
          <div className="flex gap-4 mt-6">
            {["all", "image", "video"].map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t as any)}
                className={`px-4 py-1 rounded-full text-sm ${
                  filter === t
                    ? "bg-yellow-400 text-black"
                    : "bg-white/20 text-white"
                }`}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* GRID */}
      <section ref={ref} className="py-20 bg-gray-50 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              onClick={() => setCurrentIndex(index)}
              className="relative cursor-pointer overflow-hidden rounded-xl shadow-lg group"
            >
              <img
                loading="lazy"
                src={item.type === "image" ? item.src : item.thumbnail}
                className="w-full h-48 object-cover group-hover:scale-110 transition"
              />
              {item.type === "video" && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Play className="w-10 h-10 text-white" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* MODAL */}
      <AnimatePresence>
        {currentIndex !== null && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCurrentIndex(null)}
          >
            <motion.div
              className="relative max-w-6xl w-full bg-black rounded-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setCurrentIndex(null)}
                className="absolute top-4 right-4 bg-black/70 p-2 rounded-full"
              >
                <X className="text-white" />
              </button>

              {filteredItems[currentIndex].type === "image" ? (
                <img
                  src={filteredItems[currentIndex].src}
                  className="w-full max-h-[80vh] object-contain"
                />
              ) : (
                <video
                  src={filteredItems[currentIndex].src}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh]"
                />
              )}

              <button
                onClick={prev}
                className="absolute left-3 top-1/2 bg-black/70 p-3 rounded-full"
              >
                <ChevronLeft className="text-white" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 bg-black/70 p-3 rounded-full"
              >
                <ChevronRight className="text-white" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
